import { useEffect, useState, type ReactNode } from 'react';
import type { ApiClient } from '../../../api/client';
import { text } from '../../../app/i18n';
import { Icon } from '../../../design-system/icons';
import type { InspectorView } from '../../editor/canvas-types';
import { getAt, setAt } from '../../editor/json-pointer';
import { scopeOf } from '../forms-model';
import type { Template } from '../layout-model';
import { AddButton, Choice, Fold, IconTiles, Line, OverrideMark, Rule, Stepper, Sub, Switch, ValueInput } from './controls';
import { layerLabel, type StudioCatalog } from './library';
import { LocalizedText } from './locales';
import { MediaPicker, OPTION_ICONS, type MediaChoice } from './MediaPicker';
import { PresetImpactsDialog, proposedFrom, SavePresetDialog, type PresetsPort } from './PresetDialogs';
import { detachPreset, overrideGroups, presetOf, presetRowOf, revertOverride, trackOverrides } from './presets';
import { PersonSettings } from './person-inspector';
import { FieldReactions } from './reactions';
import { BUTTON_VARIANT, changeWidget, isChoice, isNumeric, isScan, widgetOf, widgetsFor, type Entry } from './widgets';
import { setPreviewFocus } from '../preview-focus';

/**
 * Inspecteur d'un champ, éditeur v2 (ADR-146 addendum (14), (18) ; planches « EditeurV2-Contenu », « -Controles »,
 * « -Options », « -ChoisirImage », « -Reutiliser ») : trois onglets toujours visibles — Contenu (textes, widget,
 * réglages propres au widget en sections repliables, apparence, largeur, « Apparence avancée » aux jetons),
 * Contrôles (Visible, Obligatoire, Lecture seule, Non conforme en phrases ; validations), Réactions. Un champ
 * venu d'un préréglage le dit ; chaque réglage qui en diffère est « modifié ici », avec « revenir ».
 */

type Apply = (next: Template, coalesce?: string) => void;
type Json = Record<string, unknown>;

export interface FieldEnv {
  catalog: StudioCatalog;
  presets: PresetsPort;
  client?: ApiClient | undefined;
  developer: boolean;
}

/** Écrit une valeur dans une entrée (pointeur relatif) et retire les objets devenus vides. */
export function put(entry: Entry, pointer: string, value: unknown): Entry {
  let next = setAt(entry as unknown as Json, pointer, value === '' ? undefined : value);
  const segments = pointer.split('/').slice(1);
  for (let depth = segments.length - 1; depth > 0; depth -= 1) {
    const parent = `/${segments.slice(0, depth).join('/')}`;
    const node = getAt(next, parent);
    if (node !== null && typeof node === 'object' && !Array.isArray(node) && Object.keys(node).length === 0) next = setAt(next, parent, undefined);
  }
  return next as unknown as Entry;
}


const STATUS_COLORS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Non' },
  { value: 'success', label: 'Verte' },
  { value: 'danger', label: 'Rouge' },
  { value: 'warning', label: 'Orange' },
  { value: 'info', label: 'Bleue' },
  { value: 'neutral', label: 'Grise' },
];
const TOKEN_COLORS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Celle du thème' },
  { value: 'surface', label: 'Surface' },
  { value: 'surface_alt', label: 'Surface teintée' },
  { value: 'accent', label: 'Accent' },
  { value: 'neutral', label: 'Neutre' },
  { value: 'success', label: 'Succès' },
  { value: 'warning', label: 'Attention' },
  { value: 'danger', label: 'Danger' },
  { value: 'info', label: 'Information' },
  { value: 'muted', label: 'Discrète' },
];

interface Ctx {
  template: Template;
  pointer: string;
  entry: Entry;
  env: FieldEnv;
  /** Change l'entrée (et ses surcharges de préréglage) par l'historique. */
  set: (next: Entry, coalesce?: string) => void;
  /** Change un réglage de l'entrée. */
  patch: (pointer: string, value: unknown, coalesce?: string) => void;
  /** Pastille « modifié ici · revenir » d'un réglage, si l'entrée suit un préréglage. */
  mark: (prefix: string) => ReactNode;
}

function makeCtx(env: FieldEnv, template: Template, pointer: string, apply: Apply): Ctx | null {
  const entry = getAt(template, pointer) as Entry | undefined;
  if (entry === undefined) return null;
  const row = presetRowOf(entry, env.catalog.presets);
  const set = (next: Entry, coalesce?: string): void => apply(setAt(template, pointer, trackOverrides(next, row?.body)), coalesce);
  const overrides = presetOf(entry)?.overrides ?? [];
  return {
    template,
    pointer,
    entry,
    env,
    set,
    patch: (at, value, coalesce) => set(put(entry, at, value), coalesce === undefined ? undefined : `${pointer}${coalesce}`),
    mark: (prefix) => {
      const touched = overrides.filter((override) => override === prefix || override.startsWith(`${prefix}/`));
      if (row === undefined || touched.length === 0) return null;
      return <OverrideMark onRevert={() => set(touched.reduce((current, override) => revertOverride(current, row.body, override), entry))} />;
    },
  };
}

// ---- Contenu.

function PresetCard({ ctx }: { ctx: Ctx }) {
  const [impacts, setImpacts] = useState(false);
  const use = presetOf(ctx.entry);
  const row = presetRowOf(ctx.entry, ctx.env.catalog.presets);
  if (use === undefined) return null;
  const groups = overrideGroups(use.overrides);
  return (
    <section className="ay-v2-preset-card" aria-label="Préréglage">
      <span className="ay-v2-preset-card__icon" aria-hidden="true"><Icon name="copy" size={16} /></span>
      <span className="ay-v2-preset-card__text">
        <strong>Préréglage « {row === undefined ? 'introuvable' : text(row.body.label, 'fr', 'Préréglage')} »</strong>
        <span className="ay-studio__muted">
          {row === undefined ? 'Version citée absente de la bibliothèque' : `${layerLabel(ctx.env.catalog, row.origin, row.owner_ref)} · ${row.artifact_version}`}
          {groups.length > 0 && ` · ${groups.length} réglage${groups.length > 1 ? 's' : ''} modifié${groups.length > 1 ? 's' : ''} ici`}
        </span>
      </span>
      <span className="ay-v2-preset-card__actions">
        {row !== undefined && groups.length > 0 && <button type="button" className="ay-v2-link" onClick={() => setImpacts(true)}>Mettre à jour</button>}
        <button type="button" className="ay-v2-link" onClick={() => ctx.set(detachPreset(ctx.entry))} title="Garder les réglages sans suivre le préréglage">Se détacher</button>
      </span>
      {groups.length > 0 && (
        <ul className="ay-v2-preset-card__overrides">
          {groups.map((group) => (
            <li key={group.label}>
              <span>{group.label}</span>
              {row !== undefined && <button type="button" className="ay-v2-link" onClick={() => ctx.set(group.pointers.reduce((current, pointer) => revertOverride(current, row.body, pointer), ctx.entry))}>Revenir</button>}
            </li>
          ))}
        </ul>
      )}
      {impacts && row !== undefined && <PresetImpactsDialog presetId={row.artifact_id} current={row.body} proposed={proposedFrom(ctx.entry, row.body)} port={ctx.env.presets} onClose={() => setImpacts(false)} />}
    </section>
  );
}

function Texts({ ctx }: { ctx: Ctx }) {
  const { entry, pointer } = ctx;
  return (
    <>
      <LocalizedText label="Libellé" required pointer={`${pointer}/field/label`} value={entry.field.label as Record<string, string>} onChange={(value) => ctx.patch('/field/label', value ?? { fr: '' }, '/label')} />
      <LocalizedText label="Texte d’aide" multiline pointer={`${pointer}/field/description`} value={entry.field.description as Record<string, string> | undefined} onChange={(value) => ctx.patch('/field/description', value, '/description')} />
    </>
  );
}

function WidgetFold({ ctx }: { ctx: Ctx }) {
  const { entry } = ctx;
  const current = widgetOf(entry);
  const options = widgetsFor(entry.field.type);
  const presentation = (entry.presentation ?? {}) as Json;
  const object = ctx.env.catalog.model.find((candidate) => candidate.object_type === entry.field.reference_type);
  return (
    <Sub title="Widget" badge={ctx.mark('/presentation/widget')}>
      {options.length > 1 ? <IconTiles label="Widget" value={current.id} options={options.map((widget) => ({ value: widget.id, label: widget.word, hint: widget.label, icon: widget.icon }))} onChange={(id) => ctx.set(changeWidget(entry, id))} /> : <Line label="Widget"><span className="ay-v2-token"><Icon name={current.icon} size={14} /> {current.label}</span></Line>}
      {entry.field.type === 'reference' && (
        <>
          <Line label="Objet à choisir" mark={ctx.mark('/field/reference_type')}>
            <select aria-label="Objet à choisir" value={entry.field.reference_type ?? ''} onChange={(event) => ctx.patch('/field/reference_type', event.target.value)}>
              {object === undefined && <option value={entry.field.reference_type ?? ''}>{entry.field.reference_type === undefined ? 'Choisir…' : 'Objet du modèle'}</option>}
              {ctx.env.catalog.model.map((candidate) => <option key={candidate.object_type} value={candidate.object_type}>{text(candidate.label, 'fr', 'Objet')}</option>)}
            </select>
          </Line>
          {object !== undefined && (
            <Line label="Champ affiché">
              <select aria-label="Champ affiché" value={(presentation['selector'] as { label_field?: string } | undefined)?.label_field ?? ''} onChange={(event) => ctx.patch('/presentation/selector/label_field', event.target.value)}>
                <option value="">Nom de l’objet</option>
                {object.fields.filter((field) => field.type === 'string').map((field) => <option key={field.field_id} value={field.field_id}>{text(field.label, 'fr', 'champ')}</option>)}
              </select>
            </Line>
          )}
          <Switch label="Recherche" checked={(presentation['selector'] as { search?: boolean } | undefined)?.search !== false} onChange={(on) => ctx.patch('/presentation/selector/search', on ? undefined : false)} />
        </>
      )}
      {BUTTON_VARIANT.has(current.id) && (
        <Choice label="Présentation" value={(presentation['variant'] as string | undefined) ?? 'field'} options={[{ value: 'field', label: 'Champ' }, { value: 'button', label: 'Bouton' }]} onChange={(value) => ctx.patch('/presentation/variant', value === 'field' ? undefined : value)} mark={ctx.mark('/presentation/variant')} help="Bouton : un bouton compact qui ouvre le choix (« Choisir la chasse », « Scanner »)." />
      )}
    </Sub>
  );
}

function ChoicesFold({ ctx }: { ctx: Ctx }) {
  const { entry, pointer, env } = ctx;
  const [chosen, setChosen] = useState<string | null>(null);
  const [picking, setPicking] = useState<string | null>(null);
  const presentation = (entry.presentation ?? {}) as Json;
  const boolean = entry.field.type === 'boolean';
  const values = boolean ? ['true', 'false'] : (entry.field.validation?.enum_values ?? []);
  const labels = (entry.field.validation?.enum_labels ?? {}) as Record<string, Record<string, string>>;
  const labelOf = (value: string): string => (boolean ? (value === 'true' ? 'Oui' : 'Non') : text(labels[value], 'fr', value));
  const options = (presentation['options'] ?? {}) as Record<string, { image?: { file_id?: string; common_key?: string }; icon?: string; color?: string }>;
  const source = presentation['option_source'] as { object_type: string; field_id: string } | undefined;
  const sourceObject = source === undefined ? undefined : env.catalog.model.find((object) => object.object_type === source.object_type);
  const visual = (presentation['option_visual'] as string | undefined) ?? 'none';
  const selected = chosen !== null && values.includes(chosen) ? chosen : null;
  // Le choix ouvert ici est aussi celui que montre l'aperçu ; refermé ou quitté, l'aperçu revient à l'exemple.
  const fieldId = entry.field.field_id;
  useEffect(() => {
    setPreviewFocus(selected === null ? null : { fieldId, value: selected });
  }, [fieldId, selected]);
  useEffect(() => () => setPreviewFocus(null), []);
  const setOption = (value: string, key: 'image' | 'icon' | 'color', setting: unknown): void => ctx.patch(`/presentation/options/${value.replace(/~/g, '~0').replace(/\//g, '~1')}/${key}`, setting);
  const addChoice = (): void => {
    let index = values.length + 1;
    while (values.includes(`choix_${index}`)) index += 1;
    const value = `choix_${index}`;
    let next = put(entry, '/field/validation/enum_values', [...values, value]);
    next = put(next, `/field/validation/enum_labels/${value}`, { fr: `Choix ${index}` });
    ctx.set(next);
    setChosen(value);
  };
  const removeChoice = (value: string): void => {
    let next = put(entry, '/field/validation/enum_values', values.filter((candidate) => candidate !== value));
    next = put(next, `/field/validation/enum_labels/${value}`, undefined);
    next = put(next, `/presentation/options/${value}`, undefined);
    ctx.set(next);
  };
  const visuals = [
    { value: 'none', label: 'Aucun' },
    { value: 'icon', label: 'Icône' },
    { value: 'dot', label: 'Pastille' },
    { value: 'image', label: 'Image' },
    ...(source === undefined ? [] : [{ value: 'object_image', label: 'Image de l’objet' }]),
  ];
  return (
    <Sub badge={ctx.mark('/presentation/options') ?? ctx.mark('/field/validation')}>
      {source !== undefined && (
        <Line label="Source" help="Les choix, leurs libellés et leurs images viennent de ce champ du modèle.">
          <span className="ay-v2-token"><Icon name="link" size={14} /> {text(sourceObject?.label, 'fr', 'Objet du modèle')}</span>
        </Line>
      )}
      <Choice label="Visuel" value={visual} options={visuals} onChange={(value) => ctx.patch('/presentation/option_visual', value === 'none' ? undefined : value)} mark={ctx.mark('/presentation/option_visual')} />
      <ul className="ay-v2-options" aria-label="Choix">
        {values.map((value) => {
          const setting = options[value] ?? {};
          return (
            <li key={value} className={selected === value ? 'is-selected' : undefined}>
              {(visual === 'image' || visual === 'object_image') && (
                // La médiathèque s'ouvre depuis la vignette du choix (planche « Panneaux-Widgets »).
                <button type="button" className="ay-v2-options__thumb" aria-label={`Changer l’image de « ${labelOf(value)} »`} title={env.client === undefined ? 'Médiathèque indisponible ici' : 'Changer l’image'} disabled={env.client === undefined} onClick={() => setPicking(value)}>
                  {setting.color !== undefined ? <span className={`ay-v2-dot is-${setting.color}`} /> : <Icon name="image" size={14} />}
                </button>
              )}
              <button type="button" className="ay-v2-options__row" aria-pressed={selected === value} onClick={() => setChosen(selected === value ? null : value)}>
                {visual !== 'image' && visual !== 'object_image' && (
                  <span className="ay-v2-options__visual" aria-hidden="true">
                    {setting.color !== undefined ? <span className={`ay-v2-dot is-${setting.color}`} /> : setting.icon !== undefined ? <Icon name={setting.icon} size={14} /> : null}
                  </span>
                )}
                <span>{labelOf(value)}</span>
              </button>
              {!boolean && source === undefined && values.length > 1 && (
                <button type="button" className="ay-studio__icon ay-studio__icon--sm" aria-label={`Retirer le choix « ${labelOf(value)} »`} title="Retirer" onClick={() => removeChoice(value)}>
                  <Icon name="close" size={12} />
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {!boolean && source === undefined && <AddButton label="Ajouter un choix" onClick={addChoice} />}
      {source !== undefined && <p className="ay-studio__muted">Choix liés à l’objet {text(sourceObject?.label, 'fr', 'du modèle')}</p>}
      {selected !== null && (
        <div className="ay-v2-option" aria-label={`Option « ${labelOf(selected)} »`}>
          <h4 className="ay-v2-subtitle">Option « {labelOf(selected)} »</h4>
          {!boolean && <LocalizedText label="Libellé" pointer={`${pointer}/field/validation/enum_labels/${selected}`} value={labels[selected]} onChange={(value) => ctx.patch(`/field/validation/enum_labels/${selected}`, value, `/enum/${selected}`)} />}
          {(visual === 'image' || visual === 'object_image') && (
            <Line label="Image">
              <span className="ay-v2-inline">
                <button type="button" className="ay-v2-options__thumb is-large" aria-label={`Changer l’image de « ${labelOf(selected)} »`} title={env.client === undefined ? 'Médiathèque indisponible ici' : 'Changer l’image'} disabled={env.client === undefined} onClick={() => setPicking(selected)}>
                  <Icon name="image" size={16} />
                </button>
                <span className="ay-studio__muted">{options[selected]?.image !== undefined ? 'Remplacée ici' : visual === 'object_image' ? 'Celle de l’objet' : 'Aucune'}</span>
                {options[selected]?.image !== undefined && <button type="button" className="ay-v2-link" onClick={() => setOption(selected, 'image', undefined)}>{visual === 'object_image' ? 'Revenir' : 'Retirer'}</button>}
              </span>
            </Line>
          )}
          {visual === 'icon' && (
            <div className="ay-v2-icons" role="radiogroup" aria-label="Icône">
              {OPTION_ICONS.map((name) => (
                <button key={name} type="button" role="radio" aria-checked={options[selected]?.icon === name} aria-label={`Icône ${name}`} title={name} onClick={() => setOption(selected, 'icon', options[selected]?.icon === name ? undefined : name)}>
                  <Icon name={name} size={16} />
                </button>
              ))}
            </div>
          )}
          <Choice label="Pastille de couleur" value={options[selected]?.color ?? ''} options={STATUS_COLORS} onChange={(value) => setOption(selected, 'color', value === '' ? undefined : value)} />
        </div>
      )}
      {picking !== null && env.client !== undefined && (
        <MediaPicker
          client={env.client}
          title={`Image de « ${labelOf(picking)} »`}
          subtitle={visual === 'object_image' ? 'Remplace l’image de l’objet dans ce formulaire seulement' : 'Une image par choix'}
          onClose={() => setPicking(null)}
          onChoose={(choice: MediaChoice) => {
            if ('image' in choice) setOption(picking, 'image', choice.image);
            else setOption(picking, 'icon', choice.icon);
            setPicking(null);
          }}
        />
      )}
    </Sub>
  );
}

function LayoutOfChoices({ ctx }: { ctx: Ctx }) {
  const { entry } = ctx;
  const widget = widgetOf(entry).id;
  const tiles = ((entry.presentation as Json | undefined)?.['tiles'] ?? {}) as Json;
  const segmented = ((entry.presentation as Json | undefined)?.['segmented'] ?? {}) as Json;
  return (
    <Sub>
      <Choice label="Présentation" value={widget} options={[{ value: 'tiles', label: 'Tuiles' }, { value: 'segmented', label: 'Segmenté' }, ...(entry.field.type === 'enumeration' ? [{ value: 'dropdown', label: 'Liste' }] : [])]} onChange={(value) => ctx.set(changeWidget(entry, value))} />
      {widget === 'tiles' && (
        <>
          <Choice label="Tuiles par ligne" value={(tiles['per_row'] as number | 'auto' | undefined) ?? 'auto'} options={[{ value: 3, label: '3' }, { value: 4, label: '4' }, { value: 5, label: '5' }, { value: 'auto', label: 'Auto' }]} onChange={(value) => ctx.patch('/presentation/tiles/per_row', value === 'auto' ? undefined : value)} mark={ctx.mark('/presentation/tiles/per_row')} />
          <Choice label="Hauteur des tuiles" value={(tiles['height'] as string | undefined) ?? 'medium'} options={[{ value: 'small', label: 'Petite' }, { value: 'medium', label: 'Moyenne' }, { value: 'large', label: 'Grande' }]} onChange={(value) => ctx.patch('/presentation/tiles/height', value === 'medium' ? undefined : value)} mark={ctx.mark('/presentation/tiles/height')} />
          <Switch label="Visuel au-dessus du libellé" checked={tiles['visual_position'] !== 'start'} onChange={(on) => ctx.patch('/presentation/tiles/visual_position', on ? undefined : 'start')} />
          <Choice label="Tuile choisie" value={(tiles['selected_style'] as string | undefined) ?? 'outline'} options={[{ value: 'outline', label: 'Contour' }, { value: 'tint', label: 'Fond teinté' }, { value: 'filled', label: 'Plein' }]} onChange={(value) => ctx.patch('/presentation/tiles/selected_style', value === 'outline' ? undefined : value)} />
        </>
      )}
      {widget === 'segmented' && <Choice label="Choix actif" value={(segmented['selected_style'] as string | undefined) ?? 'raised'} options={[{ value: 'raised', label: 'Surélevé' }, { value: 'filled', label: 'Plein' }]} onChange={(value) => ctx.patch('/presentation/segmented/selected_style', value === 'raised' ? undefined : value)} />}
    </Sub>
  );
}

function MeasureFold({ ctx }: { ctx: Ctx }) {
  const { entry } = ctx;
  const presentation = (entry.presentation ?? {}) as Json;
  const target = (entry.target ?? {}) as Json;
  const unit = (entry.field as { unit?: string }).unit ?? (target['unit'] as string | undefined) ?? '';
  const widget = widgetOf(entry).id;
  const setUnit = (value: string): void => {
    let next = put(entry, '/field/unit', value === '' ? undefined : value);
    if (entry.target !== undefined) next = put(next, '/target/unit', value === '' ? undefined : value);
    ctx.set(next, `${ctx.pointer}/unit`);
  };
  return (
    <Sub badge={ctx.mark('/target')}>
      <Line label="Unité" mark={ctx.mark('/field/unit')}>
        <input type="text" aria-label="Unité" className="ay-v2-short" value={unit} onChange={(event) => setUnit(event.target.value)} />
      </Line>
      <Line label="Cible" stacked>
        <span className="ay-v2-inline">
          <span className="ay-v2-word">de</span>
          <Stepper label="Cible minimale" value={target['min'] as number | undefined} unit={unit} step={0.5} onChange={(value) => ctx.patch('/target/min', value, '/target/min')} />
          <span className="ay-v2-word">à</span>
          <Stepper label="Cible maximale" value={target['max'] as number | undefined} unit={unit} step={0.5} onChange={(value) => ctx.patch('/target/max', value, '/target/max')} />
        </span>
      </Line>
      <Choice label="Décimales" value={(presentation['decimals'] as number | undefined) ?? 0} options={[{ value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }]} onChange={(value) => ctx.patch('/presentation/decimals', value === 0 ? undefined : value)} mark={ctx.mark('/presentation/decimals')} />
      {(widget === 'stepper' || widget === 'slider') && (
        <Line label="Pas des boutons − / +">
          <Stepper label="Pas" value={presentation['step'] as number | undefined} unit={unit} step={0.1} min={0.1} onChange={(value) => ctx.patch('/presentation/step', value, '/step')} />
        </Line>
      )}
    </Sub>
  );
}

function ScanFold({ ctx }: { ctx: Ctx }) {
  const scan = (((ctx.entry.presentation as Json | undefined)?.['scan']) ?? {}) as Json;
  const sources = (scan['sources'] as string[] | undefined) ?? ['camera', 'wedge'];
  const mode = sources.length === 2 ? 'both' : sources[0] === 'camera' ? 'camera' : 'wedge';
  return (
    <Sub title="Lecture" badge={ctx.mark('/presentation/scan')}>
      <Choice label="Lire avec" value={mode} options={[{ value: 'camera', label: 'Caméra' }, { value: 'wedge', label: 'Douchette' }, { value: 'both', label: 'Les deux' }]} onChange={(value) => ctx.patch('/presentation/scan/sources', value === 'both' ? undefined : [value])} mark={ctx.mark('/presentation/scan/sources')} />
      <Switch label="Saisie à la main permise" checked={scan['manual_entry'] !== false} onChange={(on) => ctx.patch('/presentation/scan/manual_entry', on ? undefined : false)} />
      <Choice label="Clavier" value={(scan['keyboard'] as string | undefined) ?? 'full'} options={[{ value: 'digits', label: 'Chiffres' }, { value: 'full', label: 'Complet' }]} onChange={(value) => ctx.patch('/presentation/scan/keyboard', value === 'full' ? undefined : value)} />
      <Switch label="Bip et vibration à la lecture" checked={scan['feedback'] === true} onChange={(on) => ctx.patch('/presentation/scan/feedback', on ? true : undefined)} />
    </Sub>
  );
}

function Appearance({ ctx }: { ctx: Ctx }) {
  const presentation = (ctx.entry.presentation ?? {}) as Json;
  const empty = (presentation['empty_display'] as string | undefined) ?? 'blank';
  const button = presentation['variant'] === 'button';
  return (
    <Sub badge={ctx.mark('/presentation/size') ?? ctx.mark('/presentation/empty_display')}>
      <Choice label="Taille" value={(presentation['size'] as string | undefined) ?? 'normal'} options={[{ value: 'compact', label: 'Compacte' }, { value: 'normal', label: 'Normale' }, { value: 'large', label: 'Grande' }]} onChange={(value) => ctx.patch('/presentation/size', value === 'normal' ? undefined : value)} />
      <Choice label="Valeur en attente" value={empty} options={[{ value: 'dashes', label: '— —' }, { value: 'blank', label: 'Vide' }, { value: 'placeholder', label: 'Texte indicatif' }, { value: 'prompt', label: 'Question' }]} onChange={(value) => ctx.patch('/presentation/empty_display', value === 'blank' ? undefined : value)} help="Ce que montre le champ tant qu’il n’est pas rempli." />
      {(empty === 'placeholder' || button) && <LocalizedText label={button ? 'Texte du bouton' : 'Texte indicatif'} pointer={`${ctx.pointer}/presentation/placeholder`} value={presentation['placeholder'] as Record<string, string> | undefined} onChange={(value) => ctx.patch('/presentation/placeholder', value, '/placeholder')} />}
    </Sub>
  );
}

function SizeFold({ ctx }: { ctx: Ctx }) {
  const layout = (((ctx.entry.presentation as Json | undefined)?.['layout']) ?? {}) as Json;
  const width = layout['width'] as number | undefined;
  return (
    <Sub badge={ctx.mark('/presentation/layout')}>
      <Line label="Largeur" help="Sur une grille de 12 colonnes ; sur téléphone, toujours toute la largeur. Les poignées du canevas la règlent aussi.">
        <select aria-label="Largeur" value={width === undefined ? (layout['span'] === 'full' ? 'full' : '') : String(width)} onChange={(event) => {
          const value = event.target.value;
          let next = put(ctx.entry, '/presentation/layout/width', value === '' || value === 'full' ? undefined : Number(value));
          next = put(next, '/presentation/layout/span', value === 'full' ? 'full' : undefined);
          ctx.set(next);
        }}>
          <option value="">Auto</option>
          <option value="full">Toute la largeur</option>
          {Array.from({ length: 12 }, (_, index) => index + 1).map((value) => <option key={value} value={value}>{value}/12</option>)}
        </select>
      </Line>
    </Sub>
  );
}

function Advanced({ ctx }: { ctx: Ctx }) {
  const appearance = (((ctx.entry.presentation as Json | undefined)?.['appearance']) ?? {}) as Json;
  const set = (key: string, value: unknown): void => ctx.patch(`/presentation/appearance/${key}`, value);
  return (
    <Fold title="Avancée" open={false} summary={Object.keys(appearance).length === 0 ? undefined : `${Object.keys(appearance).length} réglage${Object.keys(appearance).length > 1 ? 's' : ''}`} badge={ctx.mark('/presentation/appearance')}>
      <Choice label="Densité" value={(appearance['density'] as string | undefined) ?? ''} options={[{ value: '', label: 'Du thème' }, { value: 'compact', label: 'Compacte', hint: 'Cibles de 36 px' }, { value: 'comfortable', label: 'Confortable', hint: 'Cibles de 44 px' }, { value: 'gloves', label: 'Gants', hint: 'Cibles de 56 px, avec des gants' }]} onChange={(value) => set('density', value === '' ? undefined : value)} />
      <Choice label="Marge intérieure" value={(appearance['padding'] as string | undefined) ?? ''} options={[{ value: '', label: 'Auto' }, { value: 'none', label: 'Aucune' }, { value: 'small', label: 'Petite' }, { value: 'medium', label: 'Moyenne' }, { value: 'large', label: 'Grande' }]} onChange={(value) => set('padding', value === '' ? undefined : value)} />
      <Choice label="Arrondi des coins" value={(appearance['radius'] as string | undefined) ?? ''} options={[{ value: '', label: 'Auto' }, { value: 'none', label: '0' }, { value: 'small', label: '6' }, { value: 'medium', label: '10' }, { value: 'large', label: '14' }]} onChange={(value) => set('radius', value === '' ? undefined : value)} />
      <Choice label="Bordure" value={(appearance['border'] as string | undefined) ?? ''} options={[{ value: '', label: 'Auto' }, { value: 'none', label: 'Aucune' }, { value: 'thin', label: 'Fine' }, { value: 'thick', label: 'Épaisse' }]} onChange={(value) => set('border', value === '' ? undefined : value)} />
      <Line label="Couleur de bordure">
        <select aria-label="Couleur de bordure" value={(appearance['border_color'] as string | undefined) ?? ''} onChange={(event) => set('border_color', event.target.value === '' ? undefined : event.target.value)}>
          {TOKEN_COLORS.map((color) => <option key={color.value} value={color.value}>{color.label}</option>)}
        </select>
      </Line>
      <Line label="Fond">
        <select aria-label="Fond" value={(appearance['background'] as string | undefined) ?? ''} onChange={(event) => set('background', event.target.value === '' ? undefined : event.target.value)}>
          {TOKEN_COLORS.map((color) => <option key={color.value} value={color.value}>{color.label}</option>)}
        </select>
      </Line>
      <Choice label="Taille du texte" value={(appearance['text_size'] as string | undefined) ?? 'normal'} options={[{ value: 'small', label: '−' }, { value: 'normal', label: 'Normale' }, { value: 'large', label: '+' }, { value: 'xlarge', label: '++' }]} onChange={(value) => set('text_size', value === 'normal' ? undefined : value)} />
      <Choice label="Étiquette" value={(appearance['label_style'] as string | undefined) ?? 'normal'} options={[{ value: 'normal', label: 'Normale' }, { value: 'bold', label: 'Gras' }, { value: 'caps', label: 'Capitales' }]} onChange={(value) => set('label_style', value === 'normal' ? undefined : value)} />
      <Switch label="Ombre portée" checked={appearance['shadow'] === true} onChange={(on) => set('shadow', on ? true : undefined)} />
      <Choice label="Alignement" value={(appearance['align'] as string | undefined) ?? 'start'} options={[{ value: 'start', label: 'Gauche' }, { value: 'center', label: 'Centre' }, { value: 'end', label: 'Droite' }]} onChange={(value) => set('align', value === 'start' ? undefined : value)} />
      {ctx.entry.target !== undefined && <Switch label="Couleur selon la cible" help="Dans la cible, près d’une limite, hors cible." checked={appearance['target_colors'] === true} onChange={(on) => set('target_colors', on ? true : undefined)} />}
    </Fold>
  );
}

/** Nom interne, en mode développeur seulement (menu ⋯ de la barre, addendum (15)). */
function Developer({ ctx }: { ctx: Ctx }) {
  if (!ctx.env.developer) return null;
  return (
    <Line label="Nom interne">
      <input type="text" readOnly value={ctx.entry.field.field_id} aria-label="Nom interne" />
    </Line>
  );
}

/** Contenu d'un champ : préréglage, textes, widget, lecture, nom interne (mode développeur), « Enregistrer comme préréglage ». */
function FieldContent({ ctx }: { ctx: Ctx }) {
  const [saving, setSaving] = useState(false);
  const { entry } = ctx;
  return (
    <>
      <PresetCard ctx={ctx} />
      <Texts ctx={ctx} />
      <WidgetFold ctx={ctx} />
      {isScan(entry) && <ScanFold ctx={ctx} />}
      <Developer ctx={ctx} />
      {presetOf(entry) === undefined && (
        <button type="button" className="ay-button ay-button--secondary ay-v2-wide" onClick={() => setSaving(true)}>
          <Icon name="copy" size={14} /> Enregistrer comme préréglage
        </button>
      )}
      {saving && <SavePresetDialog entry={entry} port={ctx.env.presets} onClose={() => setSaving(false)} />}
    </>
  );
}

const APPEARANCE_WORD: Readonly<Record<string, string>> = { compact: 'Compacte', normal: 'Normale', large: 'Grande' };

/** Résumé des contrôles d'un champ, en un mot. */
function controlsWord(entry: Entry): string {
  if (entry.field.required === true) return 'Obligatoire';
  if (entry.required_when !== undefined || (entry.visible_when !== undefined && entry.visible_when !== false) || (entry.readonly_when !== undefined && entry.readonly_when !== true) || entry.nonconformity_when !== undefined) return 'Conditionnel';
  return 'Facultatif';
}

// ---- Contrôles.

type Severity = 'block' | 'warn';
type Rule3 = 'range' | 'length' | 'pattern';

/** Retirer une validation retire aussi son « sinon avertir ». */
function withoutSeverity(entry: Entry, rule: Rule3): Entry {
  const severity: Partial<Record<Rule3, Severity>> = { ...(entry.validation_severity ?? {}) };
  delete severity[rule];
  return put(entry, '/validation_severity', Object.keys(severity).length === 0 ? undefined : severity);
}

/** « Sinon » d'une validation (inspection-template-body 1.13.0, `validation_severity`) : bloquer (défaut) ou avertir. */
function SeverityChoice({ ctx, rule }: { ctx: Ctx; rule: Rule3 }) {
  const severity = ctx.entry.validation_severity;
  const value: Severity = severity?.[rule] ?? 'block';
  const change = (next: Severity): void => {
    const rest: Partial<Record<Rule3, Severity>> = { ...(severity ?? {}) };
    if (next === 'warn') rest[rule] = 'warn';
    else delete rest[rule];
    ctx.patch('/validation_severity', Object.keys(rest).length === 0 ? undefined : rest, `/validation_severity/${rule}`);
  };
  return (
    <select className="ay-v2-severity" aria-label="Sinon" title="Si la valeur ne respecte pas la règle" value={value} onChange={(event) => change(event.target.value as Severity)}>
      <option value="block">sinon bloquer</option>
      <option value="warn">sinon avertir</option>
    </select>
  );
}

/** « Valeur au départ » (1.13.0, `initial_value`) : vide, une valeur fixe, ou la dernière saisie de l'appareil. */
function InitialValueControl({ ctx }: { ctx: Ctx }) {
  const { entry } = ctx;
  const initial = entry.initial_value;
  const mode = initial === undefined ? 'empty' : initial.source;
  const type = entry.field.type as string;
  const fixable = ['number', 'integer', 'decimal', 'string', 'boolean', 'enum', 'date'].includes(type);
  const starting = (): unknown => (type === 'boolean' ? false : type === 'number' || type === 'integer' || type === 'decimal' ? 0 : type === 'enum' ? (entry.field.validation?.enum_values?.[0] ?? '') : '');
  const choose = (next: 'empty' | 'fixed' | 'last_entry'): void => {
    ctx.patch('/initial_value', next === 'empty' ? undefined : next === 'last_entry' ? { source: 'last_entry' } : { source: 'fixed', value: starting() }, '/initial_value');
  };
  const options: Array<{ value: 'empty' | 'fixed' | 'last_entry'; label: string; hint?: string }> = [
    { value: 'empty', label: 'Vide' },
    ...(fixable ? [{ value: 'fixed' as const, label: 'Valeur fixe' }] : []),
    { value: 'last_entry', label: 'Dernière saisie', hint: 'Proposée par l’appareil, à confirmer' },
  ];
  const variable = scopeOf(ctx.template, ctx.pointer).find((candidate) => candidate.ref === `$answers.${entry.field.field_id}`);
  return (
    <>
      <Choice label="Valeur au départ" value={mode} options={options} onChange={choose} mark={ctx.mark('/initial_value')} />
      {initial?.source === 'fixed' && <ValueInput variable={variable} value={initial.value} label="Valeur" onChange={(value) => { if (value !== undefined) ctx.patch('/initial_value', { source: 'fixed', value }, '/initial_value'); }} />}
    </>
  );
}

function validationLines(ctx: Ctx): Array<{ key: string; node: ReactNode; remove: () => void }> {
  const { entry } = ctx;
  const validation = (entry.field.validation ?? {}) as Json;
  const unit = (entry.field as { unit?: string }).unit ?? '';
  const out: Array<{ key: string; node: ReactNode; remove: () => void }> = [];
  if (validation['min'] !== undefined || validation['max'] !== undefined) {
    out.push({
      key: 'range',
      node: (
        <span className="ay-v2-inline">
          <span className="ay-v2-word">La valeur doit être entre</span>
          <Stepper label="Minimum" value={validation['min'] as number | undefined} unit={unit} onChange={(value) => ctx.patch('/field/validation/min', value, '/validation/min')} />
          <span className="ay-v2-word">et</span>
          <Stepper label="Maximum" value={validation['max'] as number | undefined} unit={unit} onChange={(value) => ctx.patch('/field/validation/max', value, '/validation/max')} />
          <SeverityChoice ctx={ctx} rule="range" />
        </span>
      ),
      remove: () => ctx.set(withoutSeverity(put(put(entry, '/field/validation/min', undefined), '/field/validation/max', undefined), 'range')),
    });
  }
  if (validation['min_length'] !== undefined || validation['max_length'] !== undefined) {
    out.push({
      key: 'length',
      node: (
        <span className="ay-v2-inline">
          <span className="ay-v2-word">Entre</span>
          <Stepper label="Longueur minimale" value={validation['min_length'] as number | undefined} min={0} onChange={(value) => ctx.patch('/field/validation/min_length', value, '/validation/min_length')} />
          <span className="ay-v2-word">et</span>
          <Stepper label="Longueur maximale" value={validation['max_length'] as number | undefined} min={1} onChange={(value) => ctx.patch('/field/validation/max_length', value, '/validation/max_length')} />
          <span className="ay-v2-word">caractères</span>
          <SeverityChoice ctx={ctx} rule="length" />
        </span>
      ),
      remove: () => ctx.set(withoutSeverity(put(put(entry, '/field/validation/min_length', undefined), '/field/validation/max_length', undefined), 'length')),
    });
  }
  if (typeof validation['pattern'] === 'string') {
    const digits = /^\^\[0-9\]\{(\d+)\}\$$/.exec(validation['pattern']);
    out.push({
      key: 'pattern',
      node: digits === null ? (
        <span className="ay-v2-inline">
          <span className="ay-v2-word">Format particulier, gardé tel quel</span>
          <SeverityChoice ctx={ctx} rule="pattern" />
        </span>
      ) : (
        <span className="ay-v2-inline">
          <span className="ay-v2-word">Exactement</span>
          <Stepper label="Nombre de chiffres" value={Number(digits[1])} min={1} max={40} onChange={(value) => ctx.patch('/field/validation/pattern', value === undefined ? undefined : `^[0-9]{${value}}$`, '/validation/pattern')} />
          <span className="ay-v2-word">chiffres</span>
          <SeverityChoice ctx={ctx} rule="pattern" />
        </span>
      ),
      remove: () => ctx.set(withoutSeverity(put(entry, '/field/validation/pattern', undefined), 'pattern')),
    });
  }
  const duplicates = (entry as { duplicates?: { in_series?: boolean } }).duplicates;
  if (duplicates?.in_series === true) out.push({ key: 'unique', node: <span className="ay-v2-word">Unique dans la liste (avertissement)</span>, remove: () => ctx.patch('/duplicates/in_series', undefined) });
  return out;
}

function FieldControls({ ctx }: { ctx: Ctx }) {
  const [adding, setAdding] = useState(false);
  const { entry, template, pointer } = ctx;
  const variables = scopeOf(template, pointer).filter((variable) => variable.ref !== `$answers.${entry.field.field_id}`);
  const required = entry.field.required === true ? 'always' : entry.required_when !== undefined ? 'when' : 'never';
  const visible = entry.visible_when === undefined ? 'always' : entry.visible_when === false ? 'never' : 'when';
  const readonly = entry.readonly_when === undefined ? 'never' : entry.readonly_when === true ? 'always' : 'when';
  const nonconformity = entry.nonconformity_when === undefined ? 'never' : 'when';
  const target = entry.target as { on_out_of_target?: string } | undefined;
  const lines = validationLines(ctx);
  const inSeries = template.sections[Number(pointer.split('/')[2])]?.series !== undefined;
  const numeric = isNumeric(entry);
  const textual = entry.field.type === 'string';
  const validation = (entry.field.validation ?? {}) as Json;
  return (
    <>
      <Rule label="Visible" mode={visible} condition={entry.visible_when} variables={variables} mark={ctx.mark('/visible_when')} onChange={(mode, condition) => ctx.patch('/visible_when', mode === 'always' ? undefined : mode === 'never' ? false : condition)} />
      <Rule
        label="Obligatoire"
        mode={required}
        condition={entry.required_when}
        variables={variables}
        mark={ctx.mark('/field/required') ?? ctx.mark('/required_when')}
        onChange={(mode, condition) => {
          let next = put(entry, '/field/required', mode === 'always' ? true : undefined);
          next = put(next, '/required_when', mode === 'when' ? condition : undefined);
          ctx.set(next);
        }}
      />
      <Rule label="Lecture seule" order={['never', 'when', 'always']} mode={readonly} condition={entry.readonly_when} variables={variables} mark={ctx.mark('/readonly_when')} onChange={(mode, condition) => ctx.patch('/readonly_when', mode === 'never' ? undefined : mode === 'always' ? true : condition)} />
      <Rule label="Non conforme" order={['never', 'when']} mode={nonconformity} condition={entry.nonconformity_when} variables={scopeOf(template, pointer)} onChange={(mode, condition) => ctx.patch('/nonconformity_when', mode === 'never' ? undefined : condition)} />
      {target !== undefined && (
        <Choice label="Hors de la cible" value={target.on_out_of_target ?? 'nc_prompt'} options={[{ value: 'nc_prompt', label: 'Proposer une non-conformité' }, { value: 'nc_required', label: 'Non-conformité exigée' }, { value: 'wait', label: 'Rien' }]} onChange={(value) => ctx.patch('/target/on_out_of_target', value === 'nc_prompt' ? undefined : value)} mark={ctx.mark('/target/on_out_of_target')} />
      )}
      {entry.compute === undefined && widgetOf(entry).id !== 'person' && <InitialValueControl ctx={ctx} />}
      <Fold title="Validations de la valeur" summary={`${lines.length}`} badge={ctx.mark('/field/validation')}>
        <ul className="ay-v2-validations">
          {lines.map((line) => (
            <li key={line.key}>
              {line.node}
              <button type="button" className="ay-studio__icon ay-studio__icon--sm" aria-label="Retirer cette validation" title="Retirer" onClick={line.remove}>
                <Icon name="close" size={12} />
              </button>
            </li>
          ))}
          {lines.length === 0 && <li className="ay-studio-empty">Aucune validation.</li>}
        </ul>
        {adding ? (
          <div className="ay-v2-menu" role="menu" aria-label="Ajouter une validation">
            {numeric && validation['min'] === undefined && validation['max'] === undefined && <button type="button" role="menuitem" onClick={() => { setAdding(false); ctx.set(put(put(entry, '/field/validation/min', 0), '/field/validation/max', 10)); }}>Entre deux valeurs</button>}
            {textual && validation['min_length'] === undefined && validation['max_length'] === undefined && <button type="button" role="menuitem" onClick={() => { setAdding(false); ctx.set(put(entry, '/field/validation/max_length', 50)); }}>Longueur</button>}
            {textual && validation['pattern'] === undefined && <button type="button" role="menuitem" onClick={() => { setAdding(false); ctx.patch('/field/validation/pattern', '^[0-9]{6}$'); }}>Format : chiffres</button>}
            {inSeries && (entry as { duplicates?: { in_series?: boolean } }).duplicates?.in_series !== true && <button type="button" role="menuitem" onClick={() => { setAdding(false); ctx.patch('/duplicates/in_series', true); }}>Unicité dans la liste</button>}
            <button type="button" role="menuitem" className="ay-v2-link" onClick={() => setAdding(false)}>Annuler</button>
          </div>
        ) : (
          <AddButton label="Ajouter une validation" onClick={() => setAdding(true)} />
        )}
      </Fold>
    </>
  );
}

const choiceCount = (entry: Entry): number => (entry.field.type === 'boolean' ? 2 : (entry.field.validation?.enum_values ?? []).length);
const unitOf = (entry: Entry): string => (entry.field as { unit?: string }).unit ?? ((entry.target as { unit?: string } | undefined)?.unit ?? '');
function widthWord(entry: Entry): string {
  const layout = (((entry.presentation as Json | undefined)?.['layout']) ?? {}) as Json;
  const width = layout['width'] as number | undefined;
  return width !== undefined ? `${width}/12` : layout['span'] === 'full' ? '12/12' : 'Auto';
}

/** Contenu d'un champ « Personne » (ADR-152 §6) : Libellé et « Si vide », le texte de la puce vide. */
function PersonContent({ ctx }: { ctx: Ctx }) {
  const { entry, pointer } = ctx;
  const presentation = (entry.presentation ?? {}) as Json;
  return (
    <>
      <PresetCard ctx={ctx} />
      <LocalizedText label="Libellé" required pointer={`${pointer}/field/label`} value={entry.field.label as Record<string, string>} onChange={(value) => ctx.patch('/field/label', value ?? { fr: '' }, '/label')} />
      <LocalizedText label="Si vide" pointer={`${pointer}/presentation/placeholder`} value={presentation['placeholder'] as Record<string, string> | undefined} onChange={(value) => ctx.patch('/presentation/placeholder', value, '/placeholder')} />
      <Developer ctx={ctx} />
    </>
  );
}

/** Résumé du groupe « Personnes » : la famille de la source, en un mot. */
function peopleWord(entry: Entry): string {
  const source = (entry.field as { person_source?: { family: string } }).person_source;
  return source === undefined ? 'À régler' : source.family === 'employee_role' ? 'Employés' : 'Tiers';
}

/** Vue de l'inspecteur pour un champ : Contenu, Choix, Mesure, Disposition, Apparence, Contrôles, Réactions. */
export function fieldViewV2(env: FieldEnv, template: Template, pointer: string, apply: Apply, origin?: string): InspectorView {
  const ctx = makeCtx(env, template, pointer, apply);
  if (ctx === null) return { title: 'Champ', subtitle: 'Champ', tabs: [] };
  const { entry } = ctx;
  const section = template.sections[Number(pointer.split('/')[2])];
  const widget = widgetOf(entry);
  const count = (template.reactions ?? []).filter((reaction) => reaction.trigger.on === 'change' && reaction.trigger.field?.field_id === entry.field.field_id && reaction.trigger.field.section_id === section?.section_id).reduce((total, reaction) => total + reaction.actions.length, 0);
  const events = {
    id: 'events',
    label: 'Réactions',
    open: false,
    count,
    summary: count === 0 ? 'Aucune' : String(count),
    render: () =>
      section === undefined ? null : <FieldReactions template={template} apply={apply} catalog={env.catalog} sectionId={section.section_id} fieldId={entry.field.field_id} label={text(entry.field.label, 'fr', 'Champ')} />,
  };
  if (widget.id === 'person') {
    // Champ « Personne » (ADR-152 §6, planche « Panneaux-Personne ») : Contenu, Personnes, Contrôles.
    return {
      title: text(entry.field.label, 'fr', 'Champ'),
      subtitle: `${widget.label}${section === undefined ? '' : ` · ${text(section.label, 'fr', '')}`}`,
      ...(origin === undefined ? {} : { origin }),
      icon: widget.icon,
      tabs: [
        { id: 'content', label: 'Contenu', render: () => <PersonContent ctx={ctx} /> },
        { id: 'people', label: 'Personnes', summary: peopleWord(entry), render: () => <PersonSettings template={template} entry={entry} client={env.client} set={(next) => ctx.set(next)} patch={(at, value) => ctx.patch(at, value)} /> },
        { id: 'layout', label: 'Disposition', summary: widthWord(entry), render: () => <SizeFold ctx={ctx} /> },
        { id: 'controls', label: 'Contrôles', open: false, summary: controlsWord(entry), render: () => <FieldControls ctx={ctx} /> },
        events,
      ],
    };
  }
  return {
    title: text(entry.field.label, 'fr', 'Champ'),
    subtitle: `${widget.label}${section === undefined ? '' : ` · ${text(section.label, 'fr', '')}`}`,
    ...(origin === undefined ? {} : { origin }),
    icon: widget.icon,
    tabs: [
      { id: 'content', label: 'Contenu', render: () => <FieldContent ctx={ctx} /> },
      ...(isChoice(entry) ? [{ id: 'choices', label: 'Choix', summary: String(choiceCount(entry)), render: () => <ChoicesFold ctx={ctx} /> }] : []),
      ...(isNumeric(entry) ? [{ id: 'measure', label: 'Mesure', summary: unitOf(entry) || '—', render: () => <MeasureFold ctx={ctx} /> }] : []),
      {
        id: 'layout',
        label: 'Disposition',
        summary: widthWord(entry),
        render: () => (
          <>
            <SizeFold ctx={ctx} />
            {isChoice(entry) && ['tiles', 'segmented', 'dropdown'].includes(widget.id) && <LayoutOfChoices ctx={ctx} />}
          </>
        ),
      },
      {
        id: 'appearance',
        label: 'Apparence',
        open: false,
        summary: APPEARANCE_WORD[((entry.presentation as Json | undefined)?.['size'] as string | undefined) ?? 'normal'] ?? 'Normale',
        render: () => (
          <>
            <Appearance ctx={ctx} />
            <Advanced ctx={ctx} />
          </>
        ),
      },
      { id: 'controls', label: 'Contrôles', open: false, summary: controlsWord(entry), render: () => <FieldControls ctx={ctx} /> },
      events,
    ],
  };
}
